import React from "react";
import { Card, Typography, Box, useTheme, useMediaQuery } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import {
  CircularProgressbarWithChildren,
  buildStyles,
} from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";

interface DeviationPerformanceProps {
month: number;
year: number;
ga_code: string;
tourCardData: any;
}

const DeviationPerformance: React.FC<DeviationPerformanceProps> = ({
month,
year,
ga_code,
tourCardData,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
const { user, summary } = tourCardData || {};
const latest = summary?.[0] || null;
const territoryName = user?.territory || "";
const deviationPercent = latest?.deviation_percentage ?? 0;
const totalDeviations = latest?.total_deviations ?? 0;
const approvedDeviations = latest?.approved_deviations ?? 0;
const approvedPercent =
  totalDeviations > 0
    ? Number(((approvedDeviations / totalDeviations) * 100).toFixed(1))
    : 0;

const minThreshold = user?.min_deviation_threshold ?? 0;
const maxThreshold = user?.max_deviation_threshold ?? 100;

// Convert API decimal to percentage
const deviationPercentageValue = deviationPercent * 100;

// Determine message
let message = "";
if (deviationPercentageValue < minThreshold) {
  message = "Doing Good!";
} else if (
  deviationPercentageValue >= minThreshold &&
  deviationPercentageValue <= maxThreshold
) {
  message = "Stay Focused.";
} else {
  message = "Pay Attention.";
}



  return (
    <Card
      sx={{
        borderRadius: 3,
        border: "2px solid #2196f3",
        p: 1.2,
        minHeight: 150,
        boxShadow: "none",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{ fontSize: "0.95rem", color: "#000" }}
        >
       {territoryName}
        </Typography>
        {/* <DownloadIcon sx={{ fontSize: 18, color: "#000" }} /> */}
      </Box>

      {/* Content */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mt={0.5}
        flexWrap={isMobile ? "wrap" : "nowrap"}
        gap={1}
        pr={1.5}
      >
          
        <Box flex={1}>
          <Typography
            fontWeight={500}
            sx={{
              color: "#00b050",
              fontSize: isMobile ? "1.5rem" : "3.5rem",
              lineHeight: 1.1,
            }}
          >
            {deviationPercent}%
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mt: 0.5,
              color: "-moz-initial",
              fontWeight: 600,
              fontSize: "1.2rem",
            }}
          >
        {message}
          </Typography>

          <Typography variant="body2" color="text.secondary">
           Keep It Below {minThreshold}%.
          </Typography>
        </Box>

        {/* Right Section — Circular chart with label below */}
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          sx={{ flex: 1 }}
        >
          {/* Circular Chart */}
          <Box
            sx={{
              width: 100,
              height: 100,
              position: "relative",
            }}
          >
            <CircularProgressbarWithChildren
              value={approvedPercent}
              strokeWidth={9}
              styles={buildStyles({
                pathColor: "#00bcd4",
                trailColor: "#f5f5f5",
              })}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  textAlign: "center",
                  lineHeight: 1.1,
                  fontSize: "0.9rem",
                   fontWeight: 400, 
                }}
              >
                <strong>Total</strong>
                <br />
                <Typography
                  component="span"
                  sx={{ fontWeight: 600, fontSize: "1.25rem" }}
                >
                  {totalDeviations}
                </Typography>
              </Typography>
            </CircularProgressbarWithChildren>

            {/* Approved Percent text inside */}
            <Typography
              sx={{
                position: "absolute",
                top: 8,
                left: "50%",
                transform: "translateX(-50%)",
                color: "#00bcd4",
                fontSize: "0.75rem",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              {approvedPercent}%
            </Typography>
          </Box>

          {/* Approved Label BELOW the ring */}
          <Box
            display="flex"
            alignItems="center"
            gap={0.8}
            mt={1.2}
            sx={{ justifyContent: "center" }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: "#00bcd4",
              }}
            />
            <Typography variant="caption" sx={{ fontSize: "0.85rem" }}>
              Approved
            </Typography>
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

export default DeviationPerformance;
